import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import { DEFAULT_TRIM_PROFILE_DESCRIPTOR } from "building-types/profiles/defaultBoardTrimProfiles";
import type { GeneratedRoof } from "building-types/roofs/generatedRoofSystemTypes";
import type { BuildingGenerationIssue } from "building-types/settings/buildingGenerationIssue";
import type { ActiveProfileSource } from "building-types/settings/profileCatalogSettings";
import type { FriezeBoard } from "building-types/settings/roofBaseTypes";
import type { RoofSettings } from "building-types/settings/roofSettings";
import { EaveCutType, EaveType } from "building-types/settings/settingsTypesEnum";
import { GABLE_ROOF_THICKNESS } from "common/constants/buildingObjects";
import { supportsEaveCutType } from "doc/settingsTools";
import { MULTIGABLE_BSP_EPSILON } from "doc/utils/roofTools";
import { aabb2Height } from "geom/aabb2Types";
import { isPointInsideBSP } from "geom/bsp";
import type { NodeBSP } from "geom/bspTypes";
import { polyLine2Simplify, polyLine2AABB2, polyLine2Copy, polyLine2SetCW } from "geom/polyLine2";
import { polyLine2New } from "geom/polyLine2Types";
import { polyLine3EdgeTangent, polyLine3ReverseCopy, polyLine3StartPoint } from "geom/polyLine3";
import { vec2Normalize } from "geom/vec2";
import { pushMissingProfileReferenceIssue } from "./profileMissingReference";
import { getActiveProfileDescriptor, getProfileExtrusionDescriptorProfile } from "./profileTools";

export function getEaveDepth({ eaveFasciaSize }: RoofSettings): number {
  return eaveFasciaSize;
}

function computeSoffitDepth(eaveFasciaSize: number, soffitOffset: number = 0): number {
  return eaveFasciaSize - Math.min(Math.max(soffitOffset, 0), eaveFasciaSize - 0.1);
}

export function computeSoffitDepthFromEaveType(
  eaveType: EaveType,
  eaveFasciaSize: number,
  soffitOffset: number = 0,
): number {
  return eaveType !== EaveType.ANGLED_OPEN_OPEN
    ? computeSoffitDepth(eaveFasciaSize, soffitOffset)
    : GABLE_ROOF_THICKNESS;
}

export function getSoffitDepth({ eaveFasciaSize, soffitOffset = 0 }: RoofSettings): number {
  return computeSoffitDepth(eaveFasciaSize, soffitOffset);
}

export function getSoffitDepthFromEaveType(eaveType: EaveType, roofSettings: RoofSettings): number {
  const { eaveFasciaSize, soffitOffset = 0 } = roofSettings;
  return computeSoffitDepthFromEaveType(eaveType, eaveFasciaSize, soffitOffset);
}

export function boxEaveProfile(
  height: number,
  slope: number,
  depth: number,
  topOffset: number = 0,
  startOffset: number = 0,
  endOffset: number = 0,
) {
  const lift = slope * depth + topOffset;
  const base = -height + 2 * MULTIGABLE_BSP_EPSILON;

  return polyLine2Simplify(
    polyLine2New([
      startOffset,
      base,
      startOffset,
      topOffset,
      depth + endOffset,
      lift,
      depth + endOffset,
      base,
      startOffset,
      base,
    ]),
  );
}

export function enclosedEaveProfile(height: number, slope: number, depth: number, topOffset: number = 0) {
  const base = -height + 2 * MULTIGABLE_BSP_EPSILON;
  const y = Math.sqrt(height * height * (1 + slope * slope));

  const lift1 = slope * depth + topOffset;
  const lift2 = lift1 - y;

  return polyLine2Simplify(
    polyLine2New([0, base, 0, topOffset, depth, lift1, depth, lift2, (y - height) / slope, base, 0, base]),
  );
}

export function squaredEnclosedEaveProfile(height: number, slope: number, depth: number, topOffset: number = 0) {
  const [nX, nY] = vec2Normalize(-slope, 1);
  const y = Math.sqrt(height * height * (1 + slope * slope));

  const lift1 = slope * depth + topOffset;
  const lift2 = lift1 - y;

  return polyLine2Simplify(
    polyLine2New([-nX * height, -nY * height, 0, topOffset, depth, lift1, depth, lift2, -nX * height, -nY * height]),
  );
}

export function squaredBoxEaveProfile(height: number, slope: number, depth: number, topOffset: number = 0) {
  const [nX, nY] = vec2Normalize(-slope, 1);

  const lift1 = slope * depth + topOffset;

  return polyLine2Simplify(
    polyLine2New([
      -nX * height,
      -nY * height,
      0,
      topOffset,
      depth,
      lift1,
      depth,
      -nY * height,
      -nX * height,
      -nY * height,
    ]),
  );
}

export function squaredSlopedBoxEaveProfile(
  height: number,
  slopeAtStart: number,
  depth: number,
  topOffset: number = 0,
  startOffset: number = 0,
  endOffset: number = 0,
) {
  const [nX, nY] = vec2Normalize(-slopeAtStart, 1);

  const lift1 = slopeAtStart * (depth + endOffset) + topOffset;

  return polyLine2Simplify(
    polyLine2New([
      startOffset * nY - nX * height,
      -nY * height - startOffset * nX,
      startOffset,
      topOffset,
      depth + endOffset,
      lift1,
      depth + endOffset,
      -nY * height - startOffset * nX,
      startOffset * nY - nX * height,
      -nY * height - startOffset * nX,
    ]),
  );
}

export function squaredFasciaProfile(height: number, slope: number, depth: number, topOffset: number = 0) {
  const [nX, nY] = vec2Normalize(-slope, 1);
  const tX = nY;
  const tY = -nX;

  height *= nY;

  return polyLine2New([
    -nX * height,
    -nY * height,
    0,
    topOffset,
    tX * depth,
    tY * depth + topOffset,
    -nX * height + tX * depth,
    -nY * height + tY * depth,
    -nX * height,
    -nY * height,
  ]);
}

export function squaredMiniHipEndTrimSideProfile(
  height: number,
  depth: number,
  startSlope: number,
  startXOffset: number = 0,
  endXOffset: number = 0,
) {
  const [nX, nY] = vec2Normalize(-startSlope, 1);

  return polyLine2New([
    -nX * height + startXOffset,
    -nY * height,
    0 + startXOffset,
    0,
    depth + endXOffset,
    0,
    depth + endXOffset,
    -nY * height,
    -nX * height + startXOffset,
    -nY * height,
  ]);
}

export function plumbRafterTailExtensionSoffitProfile(height: number, slope: number, depth: number) {
  const base = -height + 2 * MULTIGABLE_BSP_EPSILON;
  return polyLine2New([
    0,
    base,
    0,
    0,
    depth,
    depth * slope - 2 * MULTIGABLE_BSP_EPSILON,
    depth,
    base + depth * slope,
    0,
    base,
  ]);
}

export function squaredEaveReturnCrossSectionProfile(
  height: number,
  slope: number,
  depth: number,
  topOffset: number = 0,
  startOffset: number = 0,
  endOffset: number = 0,
  flatTop: boolean = true,
) {
  const [nX, nY] = vec2Normalize(-slope, 1);

  const base = -height + 2 * MULTIGABLE_BSP_EPSILON;

  let topRightY = topOffset - nX * depth - 2 * MULTIGABLE_BSP_EPSILON;
  let topRightX = nY * depth + endOffset;

  if (flatTop) {
    topRightY += nX * depth;
    topRightX = depth / nY + endOffset;
  }

  return polyLine2New([
    startOffset - nX * height,
    nY * base,
    startOffset,
    topOffset,
    topRightX,
    topRightY,
    nX * base + nY * depth + endOffset,
    nY * base - nX * depth,
    startOffset - nX * height,
    nY * base,
  ]);
}

export function plumbEaveReturnCrossSectionProfile(
  height: number,
  depth: number,
  topOffset: number = 0,
  startOffset: number = 0,
  endOffset: number = 0,
) {
  const base = -height + 2 * MULTIGABLE_BSP_EPSILON;

  return polyLine2New([
    startOffset,
    base,
    startOffset,
    topOffset,
    depth + endOffset,
    topOffset,
    depth + endOffset,
    base,
    startOffset,
    base,
  ]);
}

export function squaredSlopeEaveProfile(height: number, slope: number, depth: number, topOffset: number = 0) {
  return squaredEaveReturnCrossSectionProfile(height, slope, depth, topOffset, 0, 0, false);
}

export function slopedEaveProfile(height: number, slope: number, depth: number, topOffset: number = 0) {
  const lift = slope * depth + topOffset;
  const base = -height + 2 * MULTIGABLE_BSP_EPSILON;
  return polyLine2New([0, base, 0, topOffset, depth, lift, depth, lift + base, 0, base]);
}

export function getSquaredBoxOpenSoffitBottomOffset(height: number, slope: number, depth: number) {
  const squaredProfile = squaredBoxEaveProfile(height, slope, depth);
  const boxProfile = boxEaveProfile(height, slope, depth);

  const squaredProfileAABB = polyLine2AABB2(squaredProfile);
  const boxProfileAABB = polyLine2AABB2(boxProfile);

  return aabb2Height(boxProfileAABB) - aabb2Height(squaredProfileAABB);
}

interface RoofHasCornerOptions {
  subRoof: GeneratedRoof;
  isEndSide: boolean;
  isProfileStartSide: boolean;
  overlapBSP: NodeBSP | null;
  offset?: number;
}

export function roofHasCorner({
  subRoof,
  isEndSide,
  isProfileStartSide,
  overlapBSP,
  offset = 2 * MULTIGABLE_BSP_EPSILON,
}: RoofHasCornerOptions): boolean {
  const { rails } = subRoof;
  const { leftRail, rightRail } = rails;
  let refRail = isEndSide === isProfileStartSide ? leftRail : rightRail;
  if (isEndSide) {
    refRail = polyLine3ReverseCopy(refRail);
  }

  const [tx, ty] = polyLine3EdgeTangent(refRail, 0);
  const [x, y, z] = polyLine3StartPoint(refRail);

  return roofHasCornerAtPoint({ x, y, z, tx, ty, overlapBSP, offset });
}

interface RoofHasCornerAtPointOptions {
  x: number;
  y: number;
  z: number;
  tx: number;
  ty: number;
  overlapBSP: NodeBSP | null;
  offset?: number;
}

export function roofHasCornerAtPoint({
  x,
  y,
  z,
  tx,
  ty,
  overlapBSP,
  offset = 2 * MULTIGABLE_BSP_EPSILON,
}: RoofHasCornerAtPointOptions): boolean {
  return !isPointInsideBSP(overlapBSP, x + tx * offset, y + ty * offset, z, true, MULTIGABLE_BSP_EPSILON);
}

export function hasEaveSquaredCut(eaveType: EaveType, eaveCutType: EaveCutType) {
  const isEaveSquaredCut = supportsEaveCutType[eaveType] && eaveCutType === EaveCutType.SQUARED;
  return !!isEaveSquaredCut;
}

export function hasValidFriezeBoards(friezeBoard?: FriezeBoard[]): boolean {
  if (!Array.isArray(friezeBoard) || friezeBoard.length === 0) {
    return false;
  }

  return friezeBoard.some((frieze) => frieze.friezeBoard !== "NONE");
}

export function getFriezeBoardSize(
  profileId: string,
  profileSource: ActiveProfileSource,
  issues?: BuildingGenerationIssue[],
  handle?: DocEntityHandle,
) {
  const descriptor = getActiveProfileDescriptor(profileSource, profileId, DEFAULT_TRIM_PROFILE_DESCRIPTOR);
  if (!descriptor) {
    pushMissingProfileReferenceIssue(issues, profileSource, profileId, handle, "friezeBoard");
    return 0;
  }
  const profile = getProfileExtrusionDescriptorProfile(descriptor);
  const friezeBoardProfile = polyLine2SetCW(polyLine2Simplify(polyLine2Copy(profile)));
  const friezeBoardProfileAABB = polyLine2AABB2(friezeBoardProfile);
  return aabb2Height(friezeBoardProfileAABB);
}

export function friezeBoardsTotalDepth(
  profileSource: ActiveProfileSource,
  friezeBoard?: FriezeBoard[],
  issues?: BuildingGenerationIssue[],
  handle?: DocEntityHandle,
): number | undefined {
  if (!Array.isArray(friezeBoard) || friezeBoard.length === 0 || !hasValidFriezeBoards(friezeBoard)) {
    return undefined;
  }

  return friezeBoard.reduce(
    (acc, frieze) =>
      Math.max(
        acc,
        getFriezeBoardSize(frieze.friezeBoard, profileSource, issues, handle) + (frieze.verticalOffset ?? 0),
      ),
    0,
  );
}
